import { useEffect, useState } from "react";
import { History, X } from "lucide-react";

/**
 * DocRecoveryToast — listens for `crescendo:doc-recovery-available`
 * events fired by DocumentsView when a localStorage buffer is newer
 * than the server's last save. Surfaces a non-modal banner offering to
 * restore. Used as the "disconnect / refresh recovery" safety net per
 * iter32 user request.
 */
export function DocRecoveryToast() {
  const [recovery, setRecovery] = useState(null);

  useEffect(() => {
    const onAvail = (e) => setRecovery(e.detail);
    window.addEventListener("crescendo:doc-recovery-available", onAvail);
    return () => window.removeEventListener("crescendo:doc-recovery-available", onAvail);
  }, []);

  if (!recovery) return null;

  const dismiss = () => {
    try { localStorage.removeItem(`cresc-doc-buffer:${recovery.docId}:${recovery.tabId}`); } catch {}
    setRecovery(null);
  };

  const restore = () => {
    window.dispatchEvent(new CustomEvent("crescendo:doc-recovery-restore", { detail: recovery }));
    setRecovery(null);
  };

  return (
    <div
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[300] bg-[#141414] border border-[#5A90F5] rounded-lg shadow-2xl px-4 py-3 flex items-center gap-3 max-w-md"
      data-testid="doc-recovery-toast"
    >
      <History className="w-4 h-4 text-[#5A90F5] flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-xs font-semibold text-[#EDEDED]">Unsaved changes recovered</div>
        <div className="text-[10px] text-[#A1A1AA] truncate">"{recovery.name}" — {new Date(recovery.ts).toLocaleTimeString()}</div>
      </div>
      <button
        onClick={restore}
        className="text-[10px] px-2 py-1 rounded bg-[#5A90F5] text-white font-semibold hover:bg-[#4a80e5]"
        data-testid="doc-recovery-restore"
      >
        Restore
      </button>
      <button
        onClick={dismiss}
        className="p-1 rounded hover:bg-[#1a1a1a] text-[#A1A1AA]"
        title="Discard buffered version"
        data-testid="doc-recovery-discard"
      >
        <X className="w-3 h-3" />
      </button>
    </div>
  );
}
